import { toast } from 'sonner'
import { useNotificationStore } from '@/store/notifications'

/**
 * Everything the app tells the user pops up as a toast and is also kept on the
 * notifications page.
 *
 * The toast is the immediate receipt ("Profile updated", "Discord connected",
 * why a request failed). The log is for what deserves more than a few seconds
 * -- a key you just won, an error worth quoting to support -- so a message that
 * vanished while you were looking elsewhere can still be found and copied.
 * See src/components/ui/Toasts.tsx, src/store/notifications.ts and
 * src/pages/NotificationsPage.tsx.
 */
export type NotifyColor = 'positive' | 'negative' | 'info' | 'warning'

export interface NotifyOptions {
  message: string
  color?: NotifyColor
  /** How long the toast stays up, in ms. The log entry stays until cleared. */
  duration?: number
  /**
   * An offer to fix what went wrong, shown as a button on the notification.
   * Used for refusals the user can act on — "connect your Discord account" is
   * a dead end without one.
   */
  action?: { label: string; onClick: () => void }
}

const showToast = {
  positive: toast.success,
  negative: toast.error,
  info: toast.info,
  warning: toast.warning,
} satisfies Record<NotifyColor, typeof toast.success>

/** Errors and offers to act need longer to read and reach for. */
function defaultDuration(color: NotifyColor, hasAction: boolean): number {
  if (hasAction) return 8000
  return color === 'negative' || color === 'warning' ? 5000 : 3600
}

export function notify({ message, color = 'info', duration, action }: NotifyOptions): void {
  useNotificationStore.getState().push({
    message,
    level: color,
    ...(action ? { action } : {}),
  })

  showToast[color](message, {
    duration: duration ?? defaultDuration(color, action !== undefined),
    ...(action ? { action: { label: action.label, onClick: () => action.onClick() } } : {}),
  })
}

export const notifySuccess = (message: string) => notify({ message, color: 'positive' })
export const notifyError = (message: string) => notify({ message, color: 'negative' })
